import Carousel, { EventTrackingValues } from '@carvana/ds-carousel';
import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable } from '@carvana/merch-components';
import { memo, useCallback, useContext, useEffect, useRef } from 'react';
import { elementEngaged } from '@/vdp/Analytics';
import { INTERACTION_TO_ACTION } from '~/constants/common/CarouselAnalytics';
import EventName from '~/constants/vdp/EventName';
import GalleryImageType from '~/constants/vdp/galleryImageType';
import GalleryType from '~/constants/vdp/GalleryType';
import CarouselContext from '~/context/vdp/CarouselContext';
import ModalContext from '~/context/vdp/ModalContext';
import { GalleryImage } from '~/contracts/vdp';
import useOptimizeImage from '~/hooks/common/selectors/useOptimizeImage';
import useGalleryImages from '~/hooks/vdp/useGalleryImages';
import { CarouselImage } from './MobileCarousel.styles';

const IMAGE_ID_PREFIX = 'slide-image';

function MobileCarousel() {
  const { trackEvent } = useContext(AnalyticsContext);
  const { openFullScreenGallery, slideImgId } = useContext(CarouselContext);
  const { isFullScreenGalleryOpen } = useContext(ModalContext);
  const optimizeImage = useOptimizeImage();
  const images = useGalleryImages();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isFullScreenGalleryOpen && containerRef.current) {
      const targetSlide = containerRef.current.querySelector(
        `#${IMAGE_ID_PREFIX}-${slideImgId}`
      );

      if (targetSlide) {
        targetSlide.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'start'
        });
      }
    }
  }, [isFullScreenGalleryOpen, slideImgId]);

  const handleOnClick = useCallback(
    (id: string, type: GalleryImageType) => openFullScreenGallery(id, type),
    [openFullScreenGallery]
  );

  const handleTrackEvent = useCallback(
    ({ direction, interactionType }: EventTrackingValues) => {
      const action = INTERACTION_TO_ACTION[interactionType];
      trackEvent(EventName.galleryEngaged, {
        galleryType: GalleryType.Tile
      });

      trackEvent(elementEngaged, {
        action,
        actionTaken: direction,
        galleryType: GalleryType.Tile,
        isModal: false,
        item: 'carousel',
        section: 'Gallery'
      });
    },
    [trackEvent]
  );

  if (images.length === 0) return null;

  return (
    <div data-testid="tile-carousel" ref={containerRef}>
      <Carousel
        ariaLabel="vehicle gallery images"
        classes={{
          slide: 'carousel-slide first:pl-2xl pl-lg last:pr-2xl',
          slides:
            '!gap-0 snap-proximity [-webkit-overflow-scrolling:touch] [touch-action:pan-x] [overscroll-behavior-x:contain]',
          wrapper: 'mb-2xl'
        }}
        hidePagination
        hideScrollBar
        trackEvent={handleTrackEvent}
      >
        {images.map(({ id, imageUrl, type }: GalleryImage, index: number) => {
          const maxImageWidth = 240;
          const optimizedImage = optimizeImage(imageUrl, maxImageWidth);

          return optimizedImage ? (
            <Clickable
              key={`${id}-${index}`}
              src={optimizedImage}
              onClick={() => handleOnClick(id, type)}
              element={CarouselImage}
              data-adobe-id={`gallery-select-image-${index + 1}`}
              data-analytics-attributes={{
                galleryType: GalleryType.Tile,
                imageType: type,
                isModal: false,
                item: 'image',
                section: 'Gallery'
              }}
              data-analytics-id={elementEngaged}
              data-testid="carousel-item"
              id={`${IMAGE_ID_PREFIX}-${id}`}
              loading="lazy"
              width={maxImageWidth}
            />
          ) : null;
        })}
      </Carousel>
    </div>
  );
}

export default memo(MobileCarousel);
